import { Link } from "@tanstack/react-router";
import { useMemo, useRef, useState } from "react";

import { trackEvent } from "../../lib/analytics";
import {
  bestWhatsappDigits,
  clubMessage,
  whatsappConfidence,
  mailtoHref,
  whatsappHref,
} from "../../lib/club-contact";
import { useI18n } from "../../lib/i18n";
import { shortDate, townName } from "../../lib/place";
import { createAvailabilityRequest } from "../../lib/api/lidoo.functions";
import type { Club } from "../../lib/api/lidoo.functions";

// Everything we promise on the homepage, in the FAQ and on /partner is a
// *structured request*: a date, a number of beds, a name the club can answer.
// Until now the club page delivered a bare WhatsApp link with a hardcoded
// English sentence in it, so an owner who signed up for a request inbox got
// loose chat messages instead. This form closes that gap without pretending we
// have live availability: we record the request (it shows up in the club's
// dashboard) and we hand the traveler straight to the club's own channel with
// the message already written, in the club's language.
//
// The handoff has to happen synchronously inside the click, or the browser
// treats window.open as a popup and blocks it. So: validate, open, then save.
//
// Two of the first five real requests arrived twice, seconds apart. One of the
// pair had the contact field *edited* between the two submits, which is someone
// who came back from their mail app, saw a form still sitting there fully
// filled in with an untouched button, concluded their own address was the
// problem and tried again. That is what this state machine is shaped around:
// the button has to change inside the click itself (before the server answers,
// which is the whole window in which the second press happens), and the
// confirmation has to *replace* the form rather than appear below it, because a
// form that is still there reads as a form that did not go.
//
// The confirmation also has to say the honest thing. We only open WhatsApp or
// the mail app with the message pre-written; the traveler still has to press
// send there. Claiming "sent, the club answers you directly" was a promise we
// were not keeping.

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

export function RequestPanel({ club, id = "request" }: { club: Club; id?: string }) {
  const { lang, t } = useI18n();
  const wa = bestWhatsappDigits(club);
  const tel = club.phone?.split("/")[0].trim() ?? null;
  const channels = [wa, tel, club.email].filter(Boolean).length;

  const [date, setDate] = useState("");
  const [beds, setBeds] = useState(2);
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const formRef = useRef<HTMLFormElement>(null);

  const req = useMemo(
    () => (date ? { visitDate: date, beds, name } : undefined),
    [date, beds, name],
  );

  // The channel the request actually travels down. WhatsApp first because it
  // is the one Mediterranean clubs answer within minutes; email is the honest
  // second when there is no number.
  // 87% of our WhatsApp numbers are a landline we found rather than an account
  // anyone confirmed. A wa.me link to a landline dead-ends on "this number is
  // not on WhatsApp", and someone who hits that screen leaves rather than
  // coming back to try the phone. So when the number is almost certainly a
  // landline, email leads if we have one, and the phone leads if we do not.
  // WhatsApp stays available either way: plenty of landlines do run WhatsApp
  // Business, we just stop presenting a guess as the sure route.
  const confidence = whatsappConfidence(club);
  const telHref = tel ? `tel:${tel.replace(/[^0-9+]/g, "")}` : null;
  const handoff =
    wa && confidence !== "unlikely"
      ? { kind: "whatsapp" as const, href: whatsappHref(wa, club, req) }
      : club.email
        ? { kind: "email" as const, href: mailtoHref(club.email, club, req) }
        : telHref
          ? // 189 clubs have nothing but a landline. Sending those to wa.me
            // means "this number is not on WhatsApp" and a visitor who walks
            // away. The request is still recorded, which is what the club
            // dashboard and the outreach run on; only the handoff changes, and
            // the confirmation shows the sentence to read out on the call.
            { kind: "phone" as const, href: telHref }
          : wa
            ? { kind: "whatsapp" as const, href: whatsappHref(wa, club, req) }
            : null;

  // No WhatsApp and no email: the form has nowhere to deliver to, and a form
  // that only writes to a dashboard nobody has opened yet would be a lie. Show
  // the phone, plainly.
  if (channels > 0 && !handoff) {
    return (
      <div className="rounded-2xl border border-[#1C2430]/10 bg-white/70 p-5">
        <p className="li-display text-base font-bold tracking-tight text-[#1C2430]">
          {t("contact_title")}
        </p>
        <p className="mt-0.5 text-[13px] leading-relaxed text-[#5B6472]">{t("rq_phone_only")}</p>
        <a
          href={`tel:${(tel ?? "").replace(/[^0-9+]/g, "")}`}
          onClick={() => trackEvent("phone_click", { club_slug: club.slug })}
          className="li-mono mt-4 flex h-12 items-center justify-center rounded-lg bg-[#1C2430] text-[13px] font-medium text-[#F2EFE9] transition-colors hover:bg-[#2C4FD6] active:translate-y-[1px]"
        >
          {tel}
        </a>
        <p className="li-mono mt-4 text-[11px] leading-relaxed text-[#5B6472]">{t("no_cut_note")}</p>
      </div>
    );
  }

  if (channels === 0 || !handoff) {
    return (
      <div className="rounded-2xl border border-[#1C2430]/10 bg-white/70 p-5">
        <p className="li-display text-base font-bold tracking-tight text-[#1C2430]">
          {t("contact_none_t")}
        </p>
        <p className="mt-1 text-[13px] leading-relaxed text-[#5B6472]">
          {club.website ? t("contact_none_d") : t("contact_none_bare")}
        </p>
        {club.website ? (
          <a
            href={`/go/${club.slug}`}
            target="_blank"
            rel="noopener nofollow"
            onClick={() => trackEvent("club_website_click", { club_slug: club.slug })}
            className="li-mono mt-4 flex h-12 items-center justify-center rounded-lg border border-[#1C2430]/25 text-[13px] text-[#1C2430] transition-colors hover:border-[#2C4FD6] hover:text-[#2C4FD6]"
          >
            {t("contact_website")}
          </a>
        ) : (
          <Link
            to="/partner"
            className="li-mono mt-4 flex h-12 items-center justify-center rounded-lg border border-[#1C2430]/25 text-[13px] text-[#1C2430] transition-colors hover:border-[#2C4FD6] hover:text-[#2C4FD6]"
          >
            {t("claim_cta")}
          </Link>
        )}
      </div>
    );
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    // The button is disabled from here on, so this only catches an Enter key
    // arriving before React has painted that. Cheap, and it is the guard that
    // actually has to hold: everything past this point opens a window and
    // writes a row.
    if (state !== "idle") return;
    if (!formRef.current?.reportValidity()) return;
    if (!handoff) return;

    // How the handoff opens matters, and the request data says so. Three of the
    // four duplicate submissions we have seen went through the email channel.
    // window.open on a mailto: hands the address to the mail client and leaves
    // a blank tab sitting there, so the visitor sees an empty window, concludes
    // nothing happened, and submits again. Assigning location.href instead
    // triggers the same mail client with no stray tab and no page navigation.
    //
    // WhatsApp is a real web URL and genuinely wants its own tab.
    const newTab = handoff.kind === "whatsapp";
    let opened: Window | null = null;
    if (newTab) {
      // Inside the gesture: the browser allows this, and it will not once we
      // have awaited the server call.
      opened = window.open(handoff.href, "_blank", "noopener");
    } else {
      window.location.href = handoff.href;
      opened = window; // the handoff fired; there is no popup to have been blocked
    }
    setState("sending");
    trackEvent(`request_${handoff.kind}`, {
      club_slug: club.slug,
    });

    try {
      await createAvailabilityRequest({
        data: {
          clubSlug: club.slug,
          name,
          contact,
          visitDate: date,
          partySize: beds,
          message: clubMessage(club, req),
          locale: lang,
          channel: handoff.kind,
          // The path, not the full URL: no query string, so a ?lang= or any
          // stray parameter can never end up in the requests table.
          pagePath: window.location.pathname,
        },
      });
      setState("sent");
    } catch {
      // The club already has the message if the window opened. Only say
      // something went wrong when the traveler has nothing in hand.
      setState(opened ? "sent" : "error");
    }
  }

  const field =
    "h-11 w-full rounded-lg border border-[#1C2430]/20 bg-white px-3 text-[14px] text-[#1C2430] outline-none transition-colors focus-visible:border-[#2C4FD6] focus-visible:ring-2 focus-visible:ring-[#2C4FD6]/20";

  const isWa = handoff.kind === "whatsapp";
  const isPhone = handoff.kind === "phone";

  // Sent: the form is gone, and what stands in its place says what was asked,
  // for which club, and that one thing still has to happen. The two buttons are
  // the ways back for someone whose window was swallowed by a popup blocker or
  // closed by accident.
  if (state === "sent") {
    return (
      <div id={id} className="scroll-mt-24 rounded-2xl border border-[#1C2430]/10 bg-white/70 p-5">
        <div role="status" className="rounded-xl border border-[#2C4FD6]/30 bg-[#E3EDE8] p-4">
          <span
            aria-hidden="true"
            className="flex h-8 w-8 items-center justify-center rounded-full bg-[#1C2430] text-[15px] text-[#F2EFE9]"
          >
            &#10003;
          </span>
          <p className="li-display mt-2.5 text-[17px] font-bold leading-snug tracking-tight text-[#1C2430]">
            {isWa ? t("rq_ok_title_wa") : isPhone ? t("rq_ok_title_phone") : t("rq_ok_title_mail")}
          </p>

          <dl className="mt-3 border-t border-[#1C2430]/10 text-[13.5px] text-[#1C2430]">
            {(
              [
                [t("rq_ok_club"), [club.name, townName(club.town, t)].filter(Boolean).join(", ")],
                [t("rq_date"), shortDate(date, lang)],
                [t("rq_beds"), String(beds)],
                [t("rq_ok_who"), name],
              ] as const
            ).map(([k, v]) => (
              <div
                key={k}
                className="flex justify-between gap-3.5 border-b border-[#1C2430]/10 py-1.5"
              >
                <dt className="li-mono shrink-0 text-[10.5px] uppercase tracking-wider text-[#5B6472]">
                  {k}
                </dt>
                <dd className="text-right font-semibold">{v}</dd>
              </div>
            ))}
          </dl>

          {/* The one line that decides whether the club hears anything at all. */}
          <p className="mt-3.5 flex gap-2.5 rounded-lg bg-[#F1E4DC] px-3 py-2.5 text-[13px] leading-relaxed text-[#1C2430]">
            <span aria-hidden="true" className="shrink-0 font-bold">
              !
            </span>
            <span>{isWa ? t("rq_ok_warn_wa") : isPhone ? t("rq_ok_warn_phone") : t("rq_ok_warn_mail")}</span>
          </p>

          <a
            href={handoff.href}
            target="_blank"
            rel="noreferrer"
            onClick={() => trackEvent("request_reopen", { club_slug: club.slug })}
            className="li-mono mt-3 flex h-12 items-center justify-center rounded-lg bg-[#1C2430] text-[13px] font-medium text-[#F2EFE9] transition-colors hover:bg-[#2C4FD6] active:translate-y-[1px]"
          >
            {isWa
              ? t("rq_ok_reopen_wa")
              : isPhone
                ? t("rq_ok_call", { phone: tel ?? "" })
                : t("rq_ok_reopen_mail")}
          </a>
          {isPhone ? (
            <p className="mt-3 rounded-lg bg-white/70 px-3 py-2.5 text-[13px] leading-relaxed text-[#1C2430]">
              {clubMessage(club, req)}
            </p>
          ) : null}
          {tel ? (
            <a
              href={`tel:${tel.replace(/[^0-9+]/g, "")}`}
              onClick={() => trackEvent("phone_click", { club_slug: club.slug })}
              className="li-mono mt-2 flex h-12 items-center justify-center rounded-lg border border-[#1C2430]/25 text-[13px] text-[#1C2430] transition-colors hover:border-[#2C4FD6] hover:text-[#2C4FD6] active:translate-y-[1px]"
            >
              {t("rq_ok_call", { phone: tel })}
            </a>
          ) : null}
        </div>

        <p className="li-mono mt-4 text-[11px] leading-relaxed text-[#5B6472]">
          {t("no_cut_note")}
        </p>
        <p className="li-mono mt-1.5 text-[11px] leading-relaxed text-[#5B6472]">
          {t("rq_privacy")}{" "}
          <Link to="/privacy" className="underline underline-offset-2">
            {t("nav_privacy")}
          </Link>
        </p>
      </div>
    );
  }

  return (
    <div id={id} className="scroll-mt-24 rounded-2xl border border-[#1C2430]/10 bg-white/70 p-5">
      <p className="li-display text-base font-bold tracking-tight text-[#1C2430]">
        {t("rq_title")}
      </p>
      <p className="mt-0.5 text-[13px] leading-relaxed text-[#5B6472]">
        {isWa ? t("rq_sub_wa") : isPhone ? t("rq_sub_phone") : t("rq_sub_mail")}
      </p>

      <form ref={formRef} onSubmit={submit} className="mt-4 flex flex-col gap-2.5">
        <div className="grid grid-cols-[1fr_auto] gap-2.5">
          <label className="flex flex-col gap-1">
            <span className="li-mono text-[11px] uppercase tracking-wider text-[#5B6472]">
              {t("rq_date")}
            </span>
            <input
              type="date"
              required
              min={todayISO()}
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className={field}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="li-mono text-[11px] uppercase tracking-wider text-[#5B6472]">
              {t("rq_beds")}
            </span>
            <select
              value={beds}
              onChange={(e) => setBeds(Number(e.target.value))}
              className={`${field} w-[5.5rem] pr-1`}
            >
              {[1, 2, 3, 4, 5, 6, 8, 10].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="flex flex-col gap-1">
          <span className="li-mono text-[11px] uppercase tracking-wider text-[#5B6472]">
            {t("rq_name")}
          </span>
          <input
            type="text"
            required
            maxLength={80}
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={field}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="li-mono text-[11px] uppercase tracking-wider text-[#5B6472]">
            {t("rq_contact")}
          </span>
          <input
            type="text"
            required
            maxLength={120}
            // Takes a phone number or an email, so neither type="tel" nor
            // type="email" is right. inputMode gives a keyboard with the
            // symbols both need, and spellcheck would otherwise put a red line
            // under every address someone types.
            inputMode="email"
            spellCheck={false}
            autoCapitalize="none"
            autoComplete="email tel"
            value={contact}
            onChange={(e) => setContact(e.target.value)}
            className={field}
          />
        </label>

        <button
          type="submit"
          disabled={state === "sending"}
          aria-busy={state === "sending"}
          className="li-mono mt-1.5 flex h-12 items-center justify-center gap-2 rounded-lg bg-[#1C2430] text-[13px] font-medium text-[#F2EFE9] transition-colors hover:bg-[#2C4FD6] active:translate-y-[1px] disabled:cursor-default disabled:opacity-50 disabled:hover:bg-[#1C2430]"
        >
          {state === "sending" ? (
            <>
              <span
                aria-hidden="true"
                className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-[#F2EFE9]/35 border-t-[#F2EFE9]"
              />
              {isWa ? t("rq_sending_wa") : isPhone ? t("rq_send_phone") : t("rq_sending_mail")}
            </>
          ) : isWa ? (
            t("rq_send_wa")
          ) : isPhone ? (
            t("rq_send_phone")
          ) : (
            t("rq_send_mail")
          )}
        </button>
      </form>

      {state === "error" ? (
        <p
          role="alert"
          className="li-mono mt-3 rounded-lg bg-[#F1E4DC] px-3 py-2.5 text-[12px] leading-relaxed text-[#1C2430]"
        >
          {t("rq_error")}
        </p>
      ) : null}

      {/* The direct channels stay visible. Someone who wants to phone the club
          right now should never have to fill in a form first. */}
      <div className="mt-4 flex gap-2 border-t border-[#1C2430]/10 pt-4">
        {tel ? (
          <a
            href={`tel:${tel.replace(/[^0-9+]/g, "")}`}
            onClick={() => trackEvent("phone_click", { club_slug: club.slug })}
            className="li-mono flex h-11 flex-1 items-center justify-center rounded-lg border border-[#1C2430]/25 text-[13px] text-[#1C2430] transition-colors hover:border-[#2C4FD6] hover:text-[#2C4FD6] active:translate-y-[1px]"
          >
            {t("contact_call")}
          </a>
        ) : null}
        {wa ? (
          <a
            href={whatsappHref(wa, club)}
            target="_blank"
            rel="noreferrer"
            onClick={() => trackEvent("whatsapp_click", { club_slug: club.slug })}
            className="li-mono flex h-11 flex-1 items-center justify-center rounded-lg border border-[#1C2430]/25 text-[13px] text-[#1C2430] transition-colors hover:border-[#2C4FD6] hover:text-[#2C4FD6] active:translate-y-[1px]"
          >
            {t("contact_whatsapp")}
          </a>
        ) : null}
        {club.email && !wa ? (
          <a
            href={mailtoHref(club.email, club)}
            onClick={() => trackEvent("club_mailto_click", { club_slug: club.slug })}
            className="li-mono flex h-11 flex-1 items-center justify-center rounded-lg border border-[#1C2430]/25 text-[13px] text-[#1C2430] transition-colors hover:border-[#2C4FD6] hover:text-[#2C4FD6] active:translate-y-[1px]"
          >
            {t("contact_email")}
          </a>
        ) : null}
      </div>

      {/* The fear at this exact moment is a hidden fee. Answering it here, on
          the button, is worth more than answering it in the FAQ. */}
      <p className="li-mono mt-4 text-[11px] leading-relaxed text-[#5B6472]">{t("no_cut_note")}</p>
      <p className="li-mono mt-1.5 text-[11px] leading-relaxed text-[#5B6472]">
        {t("rq_privacy")}{" "}
        <Link to="/privacy" className="underline underline-offset-2">
          {t("nav_privacy")}
        </Link>
      </p>
    </div>
  );
}

// On a 375px screen the contact panel sits roughly 3.5 screens down the club
// page, behind the gallery, the write-up, the amenities and the map. Contact
// is the only conversion this site has, so on mobile it also rides along the
// bottom of the viewport. It carries the price, because "from EUR 70" is what
// makes someone tap.
export function StickyContactBar({ club, priceText }: { club: Club; priceText: string }) {
  const { t } = useI18n();
  const wa = bestWhatsappDigits(club);
  const tel = club.phone?.split("/")[0].trim() ?? null;
  if (!wa && !tel && !club.email) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-40 border-t border-[#1C2430]/15 bg-[#F2EFE9]/95 px-4 py-3 backdrop-blur-sm lg:hidden">
      <div className="flex items-center gap-3">
        <p className="li-mono min-w-0 flex-1 truncate text-[12px] leading-tight text-[#1C2430]">
          {priceText}
        </p>
        {tel ? (
          <a
            href={`tel:${tel.replace(/[^0-9+]/g, "")}`}
            aria-label={t("contact_call")}
            onClick={() => trackEvent("phone_click", { club_slug: club.slug })}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-[#1C2430]/25 text-[#1C2430]"
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
              <path d="M5 3h3l2 5-2.5 1.5a12 12 0 0 0 6 6L15 13l5 2v3a2 2 0 0 1-2.2 2A17 17 0 0 1 3 5.2 2 2 0 0 1 5 3z" strokeLinejoin="round" />
            </svg>
          </a>
        ) : null}
        <a
          href="#request"
          className="li-mono flex h-11 shrink-0 items-center justify-center rounded-lg bg-[#1C2430] px-5 text-[13px] font-medium text-[#F2EFE9]"
        >
          {t("rq_sticky")}
        </a>
      </div>
    </div>
  );
}
